import React from 'react';

import {useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {Page} from 'components/common/Page';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {RECEIPT_STATUS_COLORS} from 'core/constants/statuses';
import {CheckStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {listState$, showConfigBlockChanged} from 'models/documents/checks/grid/list';
import {fetchCsv, tableState$} from 'models/documents/checks/grid/table';
import {breakpoint, colors} from 'theme';
import {ChecksAccessors, ChecksGridFormattedRow, ChecksGridRawRow, UserPermission} from 'types';
import {useMatchMedia, usePermission} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {GridsNavigation} from '../GridsNavigation';

import {ChecksGridList} from './List';
import {HeaderControls} from './styled';
import {ChecksGridTable} from './Table';

const getConfig = (t: Function) => [
    {
        id: ChecksAccessors.id,
        header: t('pages.documents.checks.id'),
        // filter: <Input placeholder={t('Enter value')} />,
        isSortable: false,
        width: 300,
    },
    {
        id: ChecksAccessors.createdDate,
        header: t('pages.documents.checks.createdDate'),
        // filter: <DateRange />,
        isSortable: false,
    },
    {
        id: ChecksAccessors.checkType,
        header: t('pages.documents.checks.checkType'),
        // filter: (
        //     <Select
        //         options={mapEnumToI18nOptions(CheckType, 'dictionaries.documents.checks.type')}
        //         isClearable
        //     />
        // ),
        isSortable: false,
    },
    {
        id: ChecksAccessors.seller,
        header: t('pages.documents.checks.seller'),
        // filter: <Input placeholder={t('Enter value')} />,
        isSortable: false,
    },
    {
        id: ChecksAccessors.status,
        header: t('pages.documents.checks.status'),
        // filter: (
        //     <Select
        //         options={mapEnumToI18nOptions(CheckStatus, 'dictionaries.documents.checks.status')}
        //         isClearable
        //     />
        // ),
        isSortable: false,
    },
];

/*
    t('dictionaries.documents.checks.type.1')
    t('dictionaries.documents.checks.type.2')

    t('dictionaries.documents.checks.status.DECLINED')
*/

export const exportCSV = async () => {
    const blob = await fetchCsv({gridName: 'checks'});
    saveAs(blob, `checks.csv`);
};

export const ChecksGridPage = () => {
    const {t} = useTranslation();
    const {exportPending} = useStore(tableState$);
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const {showConfigBlock} = useStore(listState$);

    const hasViewPermission = usePermission([UserPermission.RECEIPT_CARD_VIEW]);

    const convertData = (
        data: ChecksGridRawRow[] | undefined,
        t: Function,
    ): ChecksGridFormattedRow[] => {
        if (!data) return [];

        return data.map((elem) => ({
            [ChecksAccessors.id]: handleNil(elem.receiptId, (value) =>
                hasViewPermission ? (
                    <Link to={generatePath(ROUTE.RECEIPTS_DETAIL, {id: value})}>{value}</Link>
                ) : (
                    <div>{value}</div>
                ),
            ),
            [ChecksAccessors.createdDate]: handleNil(elem.createdAt, (value: string) =>
                formatDate(value, 'dd.MM.yyyy HH:mm'),
            ),
            [ChecksAccessors.seller]: handleNil(elem.manufacturerName),
            [ChecksAccessors.checkType]: handleNil(elem.operationType, (value: number) => {
                let result;
                if (value === 1 || value === 2) {
                    result = t('dictionaries.documents.checks.type.' + value);
                } else {
                    result = t('dictionaries.documents.checks.type.unsupported');
                }
                return result;
            }),
            [ChecksAccessors.status]: handleNil(elem.status, (value: CheckStatus) => (
                <StatusWrapper color={RECEIPT_STATUS_COLORS[value ?? 'DEFAULT']}>
                    {t('dictionaries.documents.checks.status.' + value)}
                </StatusWrapper>
            )),
        }));
    };

    const headerControls = (
        <HeaderControls>
            <GridsNavigation />
        </HeaderControls>
    );

    const exportConfig = {
        exportPending,
        callback: exportCSV,
    };

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : (
        undefined
    );

    return (
        <Page title={t('menu.Documents')} controls={pageHeaderControls}>
            {!isMobile && (
                <ChecksGridTable
                    headerControls={headerControls}
                    configFunc={getConfig}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                />
            )}
            {isMobile && (
                <ChecksGridList
                    headerControls={headerControls}
                    configFunc={getConfig}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                />
            )}
        </Page>
    );
};
